/**
 * Hook for fetching and transforming menu data from the API
 *
 * Handles normalized API response where products are dictionaries keyed by ID
 * and menu items reference products via product_id (FHIR-style pattern).
 *
 * The API returns:
 * - Row ingredients with `ingredient_id` instead of embedded `ingredient` object
 * - Allergy/food type references as `allergy_ids`/`food_type_ids` instead of embedded arrays
 * - Top-level `products.ingredients`, `products.allergies`, `products.food_types` dictionaries
 *
 * This hook resolves all ID references to full objects so components work unchanged.
 */
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { menuWidgetApi } from '@/store/convenienceAccessor'
import { getEnvironmentConfig } from '@/config/environment'
import { MenuItem, RowPricingRule } from '@/types/menu'
import {
  createProductLookup,
  getIngredientById,
  ProductLookup,
  ProductsDictionary,
} from '@/utils/productLookup'
import {
  getIngredientBasePrice,
  rowHasDynamicPricing,
  getComboOptionPrice,
} from '@/utils/ingredientPricing'
import { tr } from '@/utils/translate'

interface Section {
  id: string
  title: string
  description?: string
  translations?: Record<string, Record<string, string>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[]
}

interface Category {
  id: string
  name: string
}

interface Collection {
  id: string
  name: string
  description?: string
}

/**
 * Look up product from dictionary by type and ID
 */
function getProduct(
  products: ProductsDictionary | undefined,
  productType: string,
  productId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any | undefined {
  if (!products || !productId) return undefined

  switch (productType) {
    case 'meal':
      return products.meals?.[productId]
    case 'combo':
      return products.combos?.[productId]
    case 'ingredient':
      return products.ingredients?.[productId]
    default:
      return undefined
  }
}

export interface TransformedMenuData {
  menuItems: MenuItem[]
  featuredItems: MenuItem[]
  collections: Collection[]
  categories: Category[]
  itemsByCollection: Record<string, MenuItem[]>
  lookup: ProductLookup
  isLoading: boolean
  isError: boolean
  error: unknown
}

// Transform logic rules from API format
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformLogicRules(rules: any[] | undefined): any[] {
  if (!rules || !Array.isArray(rules)) return []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return rules.map((rule: any) => ({
    id: rule.id,
    name: rule.name,
    description: rule.description,
    operator: rule.operator || 'all',
    priority: rule.priority || 0,
    is_active: rule.is_active !== false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    conditions: (rule.conditions || []).map((cond: any) => ({
      id: cond.id,
      condition_type: cond.condition_type,
      trigger_row_id: cond.trigger_row_id,
      trigger_row_ingredient_id: cond.trigger_row_ingredient_id,
      metadata: cond.metadata,
    })),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    effects: (rule.effects || []).map((eff: any) => ({
      id: eff.id,
      effect_type: eff.effect_type,
      target_row_id: eff.target_row_id,
      target_row_variation_id: eff.target_row_variation_id,
      target_row_ingredient_id: eff.target_row_ingredient_id,
      metadata: eff.metadata,
    })),
  }))
}

/**
 * Transform ingredient from API format
 *
 * Handles both:
 * - Old format (embedded): { ingredient: { id, name, ... } }
 * - New format (normalized): { ingredient_id: "id" } - resolves via lookup
 */
function transformIngredient(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ing: any,
  lookup: ProductLookup,
  variationPriceMap: Record<string, Record<string, string>> | undefined,
  lang: string
) {
  // Get ingredient data - either embedded (old/mock) or via lookup (normalized API)
  const embeddedIngredient = ing.ingredient
  const resolvedIngredient = embeddedIngredient || getIngredientById(lookup, ing.ingredient_id)

  // Resolve ingredient variation group (size, etc.) for combo row ingredients
  // Variation prices come from section-level variation_price_overrides, not the ingredient itself
  const ingredientId = resolvedIngredient?.id || ing.ingredient_id
  const priceOverrides = variationPriceMap?.[ingredientId]
  const hasVariations = resolvedIngredient?.has_variations || false
  const variationGroup = hasVariations && resolvedIngredient?.variation_group
    ? {
        id: resolvedIngredient.variation_group.id,
        name: tr(resolvedIngredient.variation_group.translations, 'name', lang, resolvedIngredient.variation_group.name),
        is_required: resolvedIngredient.variation_group.is_required || false,
        variations: (resolvedIngredient.variation_group.variations || []).map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (v: any) => ({
            id: v.id,
            name: tr(v.translations, 'name', lang, v.name),
            sort_order: v.sort_order || 0,
            is_default: v.is_default || false,
            price: priceOverrides?.[v.id] !== undefined
              ? parseFloat(priceOverrides[v.id])
              : (v.price !== undefined ? parseFloat(v.price) : undefined),
          })
        ),
      }
    : undefined

  return {
    id: ing.id,
    order: ing.order || 0,
    isDefault: ing.is_default || false,
    additionalPrice: parseFloat(ing.additional_price || '0'),
    ingredient: {
      id: resolvedIngredient?.id || ing.ingredient_id || '',
      name: tr(resolvedIngredient?.translations, 'name', lang, resolvedIngredient?.name) || '',
      imageUrl: resolvedIngredient?.image_url,
      // Nutrition fields removed from API - set defaults
      calories: resolvedIngredient?.calories || 0,
      protein: resolvedIngredient?.protein || 0,
      carbs: resolvedIngredient?.carbs || 0,
      fat: resolvedIngredient?.fat || 0,
      isLogicItem: resolvedIngredient?.is_logic_item || false,
      hasNutrition: resolvedIngredient?.has_nutrition !== false,
      hasVariations,
      variationGroup,
    },
  }
}

/**
 * Transform variations from API format
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformVariations(variations: any[] | undefined, lookup: ProductLookup, lang: string) {
  if (!variations || !Array.isArray(variations)) return undefined

  // Filter out empty variations (those with empty ingredients array that aren't default)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validVariations = variations.filter((v: any) =>
    v.is_default || (v.ingredients && v.ingredients.length > 0)
  )

  if (validVariations.length === 0) return undefined

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return validVariations.map((variation: any) => ({
    id: variation.id,
    name: tr(variation.translations, 'name', lang, variation.name),
    key: variation.key,
    order: variation.order || 0,
    isDefault: variation.is_default || false,
    isActive: variation.is_active !== false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ingredients: (variation.ingredients || []).map((ri: any) =>
      transformIngredient(ri, lookup, undefined, lang)
    ),
  }))
}

/**
 * Transform modifier option from API ingredient
 *
 * Handles both embedded and normalized formats
 * Resolves ingredient variations (Small/Medium/Large) if available
 */
function transformModifierOption(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ing: any,
  lookup: ProductLookup,
  variationPriceMap: Record<string, Record<string, string>> | undefined,
  lang: string
) {
  // Get ingredient data - either embedded (old/mock) or via lookup (normalized API)
  const embeddedIngredient = ing.ingredient
  const resolvedIngredient = embeddedIngredient || getIngredientById(lookup, ing.ingredient_id)

  // Resolve ingredient variation group (size, etc.)
  // Variation prices come from section-level variation_price_overrides, not the ingredient itself
  const ingredientId = resolvedIngredient?.id || ing.ingredient_id
  const priceOverrides = variationPriceMap?.[ingredientId]
  const hasVariations = resolvedIngredient?.has_variations || false
  const variationGroup = hasVariations && resolvedIngredient?.variation_group
    ? {
        id: resolvedIngredient.variation_group.id,
        name: tr(resolvedIngredient.variation_group.translations, 'name', lang, resolvedIngredient.variation_group.name),
        is_required: resolvedIngredient.variation_group.is_required || false,
        variations: (resolvedIngredient.variation_group.variations || []).map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (v: any) => ({
            id: v.id,
            name: tr(v.translations, 'name', lang, v.name),
            sort_order: v.sort_order || 0,
            is_default: v.is_default || false,
            price: priceOverrides?.[v.id] !== undefined
              ? parseFloat(priceOverrides[v.id])
              : (v.price !== undefined ? parseFloat(v.price) : undefined),
          })
        ),
      }
    : undefined

  return {
    id: ing.id,
    name: tr(resolvedIngredient?.translations, 'name', lang, resolvedIngredient?.name) || '',
    priceAdjustment: parseFloat(ing.additional_price || '0'),
    available: true,
    isDefault: ing.is_default || false,
    isLogicItem: resolvedIngredient?.is_logic_item || false,
    hasNutrition: resolvedIngredient?.has_nutrition !== false,
    hasVariations,
    variationGroup,
  }
}

/**
 * Transform modifier group variations from API format
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformModifierVariations(variations: any[] | undefined, lookup: ProductLookup, variationPriceMap: Record<string, Record<string, string>> | undefined, lang: string) {
  if (!variations || !Array.isArray(variations)) return undefined

  // Filter out empty variations
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validVariations = variations.filter((v: any) =>
    v.is_default || (v.ingredients && v.ingredients.length > 0)
  )

  if (validVariations.length === 0) return undefined

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return validVariations.map((variation: any) => ({
    id: variation.id,
    name: tr(variation.translations, 'name', lang, variation.name),
    key: variation.key,
    order: variation.order || 0,
    isDefault: variation.is_default || false,
    isActive: variation.is_active !== false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options: (variation.ingredients || []).map((ri: any) =>
      transformModifierOption(ri, lookup, variationPriceMap, lang)
    ),
  }))
}

/**
 * Transform combo group option from API format
 *
 * Handles both:
 * - Old format (embedded): { ingredient: { id, name, ... } }
 * - New format (normalized): { ingredient_id: "id" } - resolves via lookup
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformComboOption(opt: any, lookup: ProductLookup, lang: string) {
  // Get ingredient data - either embedded (old/mock) or via lookup (normalized API)
  const embeddedIngredient = opt.ingredient
  const resolvedIngredient = embeddedIngredient || getIngredientById(lookup, opt.ingredient_id)

  return {
    id: opt.id,
    isDefault: opt.is_default || false,
    order: opt.order || 0,
    additionalPrice: parseFloat(opt.additional_price || '0'),
    ingredient: {
      id: resolvedIngredient?.id || opt.ingredient_id || '',
      name: tr(resolvedIngredient?.translations, 'name', lang, resolvedIngredient?.name) || '',
      imageUrl: resolvedIngredient?.image_url,
      // Nutrition fields removed from API - set defaults
      calories: resolvedIngredient?.calories || 0,
      protein: resolvedIngredient?.protein || 0,
      carbs: resolvedIngredient?.carbs || 0,
      fat: resolvedIngredient?.fat || 0,
    },
  }
}

/**
 * Transform API item to MenuItem format
 */
function transformApiItem(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any,
  section: Section,
  products: ProductsDictionary | undefined,
  lookup: ProductLookup,
  variationPriceMap: Record<string, Record<string, string>> | undefined,
  lang: string
): MenuItem {
  const isCombo = item.product_type === 'combo'
  // Support both embedded product (legacy/mock) and product lookup (normalized API)
  const product = item.product || getProduct(products, item.product_type, item.product_id)

  // Parse section-level pricing fields
  // API sends customization_prices keyed by ingredient_id, but downstream code
  // looks up by row-ingredient junction id (ri.id / option.id). Remap the keys
  // so all lookups match.
  let customizationPrices: Record<string, number> | undefined
  if (item.customization_prices) {
    const rawPrices = item.customization_prices as Record<string, string | number>
    const remapped: Record<string, number> = {}
    // Build ingredient_id → junction id mapping from product rows
    const rows = product?.rows as { ingredients: { id: string; ingredient_id: string }[] }[] | undefined
    if (rows) {
      for (const row of rows) {
        for (const ri of row.ingredients || []) {
          const price = rawPrices[ri.ingredient_id]
          if (price !== undefined) {
            remapped[ri.id] = typeof price === 'number' ? price : parseFloat(price)
          }
        }
      }
    }
    // Keep any keys that didn't match (fallback for other formats)
    const mappedIngredientIds = new Set(
      rows?.flatMap(r => (r.ingredients || []).map(ri => ri.ingredient_id)) || []
    )
    for (const [k, v] of Object.entries(rawPrices)) {
      if (!mappedIngredientIds.has(k)) {
        remapped[k] = typeof v === 'number' ? v : parseFloat(v)
      }
    }
    customizationPrices = Object.keys(remapped).length > 0 ? remapped : undefined
  }

  const rowPricingRules: Record<string, RowPricingRule> | undefined =
    item.row_pricing_rules
      ? Object.fromEntries(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Object.entries(item.row_pricing_rules as Record<string, any>).map(([k, v]) => [
            k,
            {
              price: typeof v.price === 'number' ? v.price : parseFloat(v.price || '0'),
              freeQuantity: v.free_quantity ?? 0,
              label: v.label,
            },
          ])
        )
      : undefined

  const comboOptionPrices: Record<string, number> | undefined =
    item.combo_option_prices
      ? Object.fromEntries(
          Object.entries(item.combo_option_prices as Record<string, string | number>).map(
            ([k, v]) => [k, typeof v === 'number' ? v : parseFloat(v)]
          )
        )
      : undefined

  // Build a partial MenuItem for pricing lookups during transform
  const pricingStub = {
    customizationPrices,
    rowPricingRules,
    comboOptionPrices,
  } as MenuItem

  // Transform combo product structure
  let comboProduct = undefined
  if (isCombo && product) {
    comboProduct = {
      mainMeal: product.main_meal ? {
        id: product.main_meal.id,
        title: tr(product.main_meal.translations, 'title', lang, product.main_meal.title),
        description: tr(product.main_meal.translations, 'description', lang, product.main_meal.description) || '',
        imageUrl: product.main_meal.image_url,
        mealType: product.main_meal.meal_type || 'fixed',
        basePrice: parseFloat(product.main_meal.base_price || '0'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rows: (product.main_meal.rows || []).map((row: any) => {
          const isDynamic = rowHasDynamicPricing(pricingStub, row.id)
          return {
            id: row.id,
            title: tr(row.translations, 'title', lang, row.title),
            description: row.description || '',
            selectionType: row.selection_type === 'quantity' ? 'quantity' as const : 'multiple' as const,
            minSelections: row.min_selections ?? 0,
            maxSelections: row.max_selections || 1,
            maxQuantityPerItem: row.max_quantity_per_item ?? null,
            order: row.order || 0,
            canBeHidden: row.can_be_hidden,
            isPersistent: row.is_persistent || false,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ingredients: (row.ingredients || []).map((ri: any) => {
              const base = transformIngredient(ri, lookup, variationPriceMap, lang)
              // Static price override for non-dynamic rows
              if (!isDynamic && (customizationPrices || rowPricingRules)) {
                base.additionalPrice = getIngredientBasePrice(pricingStub, row.id, ri.id)
              }
              return base
            }),
            variations: transformVariations(row.variations, lookup, lang),
          }
        }),
        logicRules: transformLogicRules(product.main_meal.logic_rules),
      } : undefined,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      groups: (product.groups || []).map((group: any) => ({
        id: group.id,
        name: tr(group.translations, 'name', lang, group.name),
        minSelections: group.min_selections || 0,
        maxSelections: group.max_selections || 1,
        order: group.order || 0,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options: (group.options || []).map((opt: any) => {
          const base = transformComboOption(opt, lookup, lang)
          // Apply combo option price override
          if (comboOptionPrices) {
            base.additionalPrice = getComboOptionPrice(pricingStub, opt.id, base.additionalPrice)
          }
          return base
        }),
      })),
    }
  }

  // Transform logic_rules for single meals
  const logicRules = !isCombo && product?.logic_rules
    ? transformLogicRules(product.logic_rules)
    : undefined

  // Resolve ingredient variation group (size, etc.)
  // Merge variation names from product dictionary + prices from section item
  let ingredientVariationGroup: ReturnType<typeof transformApiItem>['ingredientVariationGroup']
  if (item.product_type === 'ingredient') {
    const ingredientProduct = products?.ingredients?.[item.product_id]
    const priceOverrides = item.variation_price_overrides as Record<string, string> | undefined
    if (ingredientProduct?.has_variations && ingredientProduct.variation_group) {
      ingredientVariationGroup = {
        id: ingredientProduct.variation_group.id,
        name: tr(ingredientProduct.variation_group.translations, 'name', lang, ingredientProduct.variation_group.name),
        is_required: ingredientProduct.variation_group.is_required || false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        variations: (ingredientProduct.variation_group.variations || []).map((v: any) => ({
          id: v.id,
          name: tr(v.translations, 'name', lang, v.name),
          sort_order: v.sort_order || 0,
          is_default: v.is_default || false,
          price: priceOverrides?.[v.id] !== undefined ? parseFloat(priceOverrides[v.id]) : undefined,
        })),
      }
    } else if (priceOverrides) {
      const entries = Object.entries(priceOverrides)
        .map(([id, price]) => ({ id, price: parseFloat(price) }))
        .sort((a, b) => a.price - b.price)

      ingredientVariationGroup = {
        id: item.product_id || item.id,
        name: 'Size',
        is_required: true,
        variations: entries.map((e, idx) => ({
          id: e.id,
          name: `$${e.price.toFixed(2)}`,
          sort_order: idx,
          is_default: idx === 0,
          price: e.price,
        })),
      }
    }
  }

  return {
    id: item.id,
    name: tr(item.translations, 'display_name', lang, item.title),
    description: tr(item.translations, 'short_description', lang, item.short_description) || '',
    price: parseFloat(item.price || item.base_price || '0'),
    currency: item.currency,
    image_url: item.product_image_url,
    isPopular: item.is_featured || item.featured || product?.is_featured || product?.featured || item.badge_labels?.includes('Popular'),
    isFeatured: item.is_featured || item.featured || product?.is_featured || product?.featured || false,
    badgeLabels: item.badge_labels || [],
    available: true,
    itemType: isCombo ? 'combo' : 'single',
    collection_id: section.id,
    category: tr(section.translations, 'title', lang, section.title),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    modifierGroups: !isCombo && product?.rows ? product.rows.map((row: any) => {
      const minSel = row.min_selections ?? 0
      const isDynamic = rowHasDynamicPricing(pricingStub, row.id)
      const selType = row.selection_type === 'quantity'
        ? 'quantity' as const
        : (row.max_selections || 1) > 1 ? 'multiple' as const : 'single' as const
      return {
        id: row.id,
        name: tr(row.translations, 'title', lang, row.title),
        selectionType: selType,
        required: minSel > 0 || selType === 'single',
        minSelections: selType === 'single' ? Math.max(minSel, 1) : minSel,
        maxSelections: row.max_selections || 1,
        maxQuantityPerItem: row.max_quantity_per_item ?? null,
        canBeHidden: row.can_be_hidden,
        isPersistent: row.is_persistent || false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options: (row.ingredients || []).map((ri: any) => {
          const opt = transformModifierOption(ri, lookup, variationPriceMap, lang)
          // Static price override for non-dynamic rows
          if (!isDynamic && (customizationPrices || rowPricingRules)) {
            opt.priceAdjustment = getIngredientBasePrice(pricingStub, row.id, ri.id)
          }
          return opt
        }),
        variations: transformModifierVariations(row.variations, lookup, variationPriceMap, lang),
      }
    }) : [],
    comboProduct,
    logicRules,
    ingredientVariationGroup,
    customizationPrices,
    rowPricingRules,
    comboOptionPrices,
    allergens: [],
    foodTypes: [],
  }
}

export function useTransformedMenuData(): TransformedMenuData {
  const config = getEnvironmentConfig()
  const { i18n } = useTranslation()
  const lang = i18n.language

  const { data: menuConfig, isLoading, isError, error } = menuWidgetApi.menuConfig.get({
    restaurant_id: config.restaurantId,
  })

  const sections = useMemo((): Section[] => {
    // Resolve menus from the entities dictionary and pick the first one
    // that actually has sections referenced.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allMenuIds: string[] = menuConfig?.result?.menus || []
    const menuWithSections = allMenuIds
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((id: string) => menuConfig?.entities?.menus?.[id])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .find((m: any) => m?.sections?.length > 0)

    if (!menuWithSections) return []

    // Resolve section IDs into section entities. Item IDs inside each
    // section stay as strings here — they're resolved in the flatMap below.
    return (menuWithSections.sections || [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((sId: string) => menuConfig?.entities?.sections?.[sId])
      .filter(Boolean) as Section[]
  }, [menuConfig])

  // Get products dictionary for normalized API lookup
  const products = useMemo(() => {
    return menuConfig?.entities?.products as ProductsDictionary | undefined
  }, [menuConfig])

  // Create lookup maps for resolving ID references
  const lookup = useMemo(() => {
    return createProductLookup(products)
  }, [products])

  // Build a map of ingredient ID → variation_price_overrides from section items
  // This lets us resolve variation prices when ingredients appear as modifier options
  // Section.items is now an array of item ID strings — resolve each via entities.items
  const variationPriceMap = useMemo(() => {
    const map: Record<string, Record<string, string>> = {}
    for (const section of sections) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const itemIds = (section.items || []) as any[]
      for (const iId of itemIds) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const item: any = menuConfig?.entities?.items?.[iId as string]
        if (!item) continue
        if (item.product_type === 'ingredient' && item.variation_price_overrides && item.product_id) {
          if (!map[item.product_id]) {
            map[item.product_id] = item.variation_price_overrides
          }
        }
      }
    }
    return map
  }, [sections, menuConfig])

  const menuItems = useMemo(() => {
    if (!sections.length) return []
    const items = sections.flatMap((section) =>
      (section.items || [])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((iId: any) => menuConfig?.entities?.items?.[iId as string])
        .filter(Boolean)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((item: any) => transformApiItem(item, section, products, lookup, variationPriceMap, lang))
    )
    return items
  }, [sections, products, lookup, variationPriceMap, menuConfig, lang])

  const collections = useMemo(() => {
    return sections.map((section) => ({
      id: section.id,
      name: tr(section.translations, 'title', lang, section.title),
      description: section.description,
    }))
  }, [sections, lang])

  const categories = useMemo(() => {
    if (!menuConfig?.entities?.categories) return []
    return menuConfig.entities.categories as Category[]
  }, [menuConfig])

  const itemsByCollection = useMemo(() => {
    return menuItems.reduce((acc: Record<string, MenuItem[]>, item) => {
      const collectionId = item.collection_id
      if (!acc[collectionId]) {
        acc[collectionId] = []
      }
      acc[collectionId].push(item)
      return acc
    }, {})
  }, [menuItems])

  // Filter featured items for carousel
  const featuredItems = useMemo(() => {
    return menuItems.filter((item) => item.isFeatured)
  }, [menuItems])

  return {
    menuItems,
    featuredItems,
    collections,
    categories,
    itemsByCollection,
    lookup,
    isLoading,
    isError,
    error,
  }
}
